/**
 * P2-08: Pins —— 固定资源/编队面板（迁移自 WPins，js/jsx/left.jsx.js:757-833）。
 *
 * - 数据只读消费：`race.pinned` 存于种族对象（diplomacy.js:38, 242 随存档序列化），
 *   `loadout.pinned` 存于 loadout 对象（village.js:3432, 3468/3478 save/load）；
 *   pin/unpin 入口在 Trade/Village tab，左栏**没有编辑模式**（INVENTORY "WPins 机制"）——
 *   dev-plan 中"与编辑模式"由 ResourceTable（P2-07）与 ResourceRow 行级复选框（P2-05）承载；
 * - 交互：种族 pin 点击 → game.diplomacy.tradeAll(race)（left.jsx.js:768-771）；
 *   loadout pin 点击 → loadout.setLoadout(true)（left.jsx.js:781-783）；
 *   点击时按 name/index 重新解析引擎对象，避免闭包持有过期引用；
 * - 图标：tradeIcons 代理映射逐字保留（slab→minerals、scaffold→wood）；
 *   无映射（如 leviathan）→ 无图标无箭头（left.jsx.js:794-803, 814-815）；
 * - 无 pinned 项时 CollapsiblePanel 本身仍渲染、内部为空（left.jsx.js:829-831）。
 */
import { useCallback } from "react";
import type { ReactNode } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import { CollapsiblePanel } from "../../components/CollapsiblePanel";

// Maps a traded resource to its svg icon; slab/scaffold reuse minerals/wood as proxies.
const TRADE_ICONS: Record<string, string> = {
  minerals: "minerals",
  iron: "iron",
  wood: "wood",
  catnip: "catnip",
  titanium: "titanium",
  coal: "coal",
  uranium: "uranium",
  ivory: "ivory",
  slab: "minerals",
  scaffold: "wood",
};

/** Returns the masked icon for a resource, or null when no icon is mapped (e.g. leviathan trades). */
function renderResourceIcon(name: string): ReactNode {
  const icon = TRADE_ICONS[name];
  if (!icon) {
    return null;
  }
  return (
    <div
      className="svg-icon res-icon"
      style={{
        WebkitMask: "url('res/img/resources/" + icon + ".svg') no-repeat center / contain",
        mask: "url('res/img/resources/" + icon + ".svg') no-repeat center / contain",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// 快照：pinned 种族 + pinned loadout（引擎顺序，只读消费）
// ---------------------------------------------------------------------------

export type PinSnapshot =
  | { kind: "race"; name: string; title: string; buy: string; sell: string }
  | { kind: "loadout"; index: number; label: string };

export function computePins(game: GamePage | null): PinSnapshot[] | null {
  if (!game) {
    return null;
  }
  const pins: PinSnapshot[] = [];
  for (const race of game.diplomacy.races) {
    if (race.pinned) {
      pins.push({
        kind: "race",
        name: race.name,
        title: race.title,
        buy: race.buys[0].name,
        sell: race.sells[0].name,
      });
    }
  }
  const loadouts = game.village.loadoutController.loadouts;
  for (let i = 0; i < loadouts.length; i++) {
    const loadout = loadouts[i];
    if (loadout.pinned) {
      pins.push({ kind: "loadout", index: i, label: t("left.loadout.do", [loadout.title]) });
    }
  }
  return pins;
}

function pinsEq(a: PinSnapshot[] | null, b: PinSnapshot[] | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b || a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    const pa = a[i];
    const pb = b[i];
    if (pa.kind !== pb.kind) {
      return false;
    }
    if (pa.kind === "race" && pb.kind === "race") {
      if (pa.name !== pb.name || pa.title !== pb.title || pa.buy !== pb.buy || pa.sell !== pb.sell) {
        return false;
      }
    }
    if (pa.kind === "loadout" && pb.kind === "loadout") {
      if (pa.index !== pb.index || pa.label !== pb.label) {
        return false;
      }
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

export function Pins() {
  const selector = useCallback((game: GamePage | null) => computePins(game), []);
  const pins = useGameTick(selector, pinsEq);

  if (!pins) {
    return null;
  }

  const onClickRace = (name: string) => {
    const game = getGame();
    if (!game) {
      return;
    }
    for (const race of game.diplomacy.races) {
      if (race.name === name) {
        game.diplomacy.tradeAll(race);
        return;
      }
    }
  };

  const onClickLoadout = (index: number) => {
    const loadout = getGame()?.village.loadoutController.loadouts[index];
    loadout?.setLoadout(true);
  };

  return (
    <CollapsiblePanel title={t("left.trade")}>
      {pins.map((pin) => {
        let content: ReactNode;
        let key: string;
        let onClick: () => void;
        if (pin.kind === "race") {
          const buy = renderResourceIcon(pin.buy);
          const sell = renderResourceIcon(pin.sell);
          //Arrow only makes sense between two icons; skip it otherwise.
          const arrow = buy && sell ? <span className="trade-arrow">··</span> : null;
          content = [
            <span className="trade-icons" key="icons">
              {buy}
              {arrow}
              {sell}
            </span>,
            <span key="label">{t("left.trade") + " " + pin.title}</span>,
          ];
          key = "race:" + pin.name;
          onClick = () => onClickRace(pin.name);
        } else {
          content = pin.label;
          key = "loadout:" + pin.index;
          onClick = () => onClickLoadout(pin.index);
        }
        return (
          <div className="pin-link" key={key}>
            <a href="#" onClick={onClick}>
              {content}
            </a>
          </div>
        );
      })}
    </CollapsiblePanel>
  );
}

/**
 * 独立挂载入口，仅供隔离测试/调试。pinned 随存档持久化（种族/loadout
 * 对象上），本面板只读消费。P2-20 起生产挂载由 LeftPanel 统一承担，
 * 本入口不再注册到 KGNewUI.panels。
 */
export default function PinsPanel() {
  return <Pins />;
}
